// Layout.jsx
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { notification, Drawer } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';

import TaskSidePanel from '../UI/organisms/TaskSidePanel';
import ChatDrawer from '../UI/organisms/ChatDrawer';
import AiSidePanel from '../UI/organisms/AiSidePanel';
import UserChatPanel from '../UI/organisms/UserChatPanel';

import LeftNavBar from '../UI/organisms/LeftNavBar';
import TopNavbar from '../UI/molecules/upper-navbar/TopNavbar';

import WorkspaceFieldsModal from '../UI/molecules/modals/WorkspaceFieldsModal';
import DuplicateProjectModal from '../UI/molecules/modals/DuplicateProjectModal';
import WorkspaceTagsModal from '../UI/molecules/modals/WorkspaceTagsModal';

import {
  getWorkspaceDefaultFilter,
  resendConfirmEmail,
  acceptWorkspaceInvite,
  getWorkspaceById,
  getWorkspaces,
  getSingleMessage,
  isChatOpen as chatOpener,
  isTicketOpen as ticketOpener,
  unsetActiveProject,
  getProjectById,
  getProjectCustomField,
  joinProject,
} from '../../actions';
import { getTasksAction } from '../../redux/tasks/actions';
import { getUser, verifyUser, setUnauthorized } from '../../actions/userAction';
import {
  getWorkspaceDashboardData,
  getWorkspaceInvites,
  getWorkspaceTags,
} from '../../actions/WorkspaceActions';
import { resetDrawer } from '../../actions/toggleAction';
import { isDrawerOpen } from '../../reducers/toggleReducer';
import { Mobile } from './Mobile';

import { APPLICATION_NAME, MOBILE_BREAKPOINT } from '../../js/constants';

import Loader from '../UI/atoms/global/Loader';
import AddChatDrawer from '../../src-new/drawers/AddChat';
import UserDrawer from '../../src-new/drawers/UserDrawer';
import AddProject from '../../src-new/drawers/AddProject';
import MemberDrawer from '../../src-new/drawers/MemberDrawer';
import AddTaskDrawer from '../../src-new/drawers/AddTask';
import { FormattedMessage, useIntl } from 'react-intl';
import { getOpenedTask } from '../../redux/tasks/taskEntities/selectors';
import { logoutAction } from '../../redux/general/actions';
import { getUserTasksPage } from '../../redux/general/selectors';
import InviteToProjectModal from '../UI/molecules/modals/InviteToProjectModal';
import { getTaskAction } from '../../redux/tasks/actions';
import InviteToWorkspaceModal from '../UI/molecules/modals/InviteToWorkspaceModal';
import { fetchNotifications } from '../../redux/state-areas/notifications/notificationActions';
import { socketSendAction } from '../../redux/state-areas/socket/actions';
import EditProjectDetailsModal from '../UI/molecules/modals/EditProjectDetailsModal';
import PortfolioModal from '../UI/molecules/modals/PortfolioModal';
import EditCustomFieldModal from '../UI/molecules/modals/EditCustomFieldModal';
import DuplicateTaskModal from '../UI/molecules/modals/DuplicateTaskModal';
import CustomChartModal from '../UI/molecules/dashboard-widgets/modals/CustomChartModal';
import ChartFullScreenModal from '../UI/molecules/modals/ChartFullScreenModal';
import SettingsModal from '../UI/molecules/modals/SettingsModal';

const Layout = (props) => {
  const {
    projectCustomFields,
    children,
    phase = 'home',
    currentWorkspace,
    workspaceTags,
  } = props;

  const intl = useIntl();

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { logout: auth0Logout } = useAuth0();

  const [user, setUser] = useState(true);
  const existingUserRef = useRef(useSelector((state) => state.userReducer.user));
  const existingWorkspacesRef = useRef(useSelector((state) => state.workspaceReducer.getWorkspaces));

  // On the all-tasks page the ~2MB dashboard bootstrap and the /filter request
  // fire together; on the single-threaded backend the dashboard build blocks
  // /filter for ~900ms. We defer the dashboard there until the first tasks page
  // has loaded (or a fallback timer) so /filter hits an idle backend.
  const isAllTasksPage = phase === 'all-tasks';
  const userTasksLoaded = useSelector((state) => Boolean(getUserTasksPage(state)));
  const dashboardBootstrappedRef = useRef(false);

  const showDrawer = useSelector((state) => isDrawerOpen(state));
  const workspaceFieldsModal = useSelector((state) => state.modalReducer.workspaceFieldsModal);
  const workspaceTagsModal = useSelector((state) => state.modalReducer.workspaceTagsModal);
  const duplicateProjectModal = useSelector((state) => state.modalReducer.duplicateProjectModal);
  const inviteToProjectModal = useSelector((state) => state.modalReducer.inviteToProjectModal);
  const inviteToWorkspaceModal = useSelector((state) => state.modalReducer.inviteToWorkspaceModal);
  const portfolioModal = useSelector((state) => state.modalReducer.portfolioModal);
  const editProjectDetailsModal = useSelector((state) => state.modalReducer.editProjectDetailsModal);
  const duplicateTaskModal = useSelector((state) => state.modalReducer.duplicateTaskModal);
  const chartFullScreenModal = useSelector((state) => state.modalReducer.chartFullScreenModal);

  const unauthorized = useSelector((state) => state.userReducer.unauthorized);
  const forbidden = useSelector((state) => state.userReducer.forbidden);

  const isChatSettingsOpen = useSelector((state) => state.toggleReducer.isChatSettingsOpen);
  const isTeamDrawerState = useSelector((state) => state.toggleReducer.teamDrawerState);
  const isChatOpen = useSelector((state) => state.toggleReducer.isChatOpen);
  const isTicketOpen = useSelector((state) => state.toggleReducer.isTicketOpen);
  const isAiOpen = useSelector((state) => state.toggleReducer.isAiOpen);
  const activeChatMessage = useSelector((state) => state.toggleReducer.activeChatMessage);
  const isUserDrawerOpen = useSelector((state) => state.toggleReducer.isUserDrawerOpen);
  const drawerWidthRef = useRef(window.innerWidth > 600 ? 550 : '90%');
  if (showDrawer) {
    drawerWidthRef.current = isUserDrawerOpen ? 300 : (window.innerWidth > 600 ? 550 : '90%');
  }
  const isMemberDrawerOpen = useSelector((state) => state.toggleReducer.isMemberDrawerOpen);
  const isWorkspaceInvites = useSelector((state) => state.toggleReducer.isWorkspaceInvites);
  const isJoinProjects = useSelector((state) => state.toggleReducer.isJoinProjects);
  const drawerWidth = useSelector((state) => state.toggleReducer.drawerWidth);

  const selectedTaskParam = new URLSearchParams(location.search).get(
    'selectedTask',
  );

  const singleTask = useSelector((state) => getOpenedTask(state));
  const taskIdentifier = singleTask?.titleIdentifier;
  const { singleMessage } = useSelector((state) => state.messageReducer);
  const { 1: chatId } = window.location.pathname.split('/');
  const msgId = new URLSearchParams(location.search).get('msgId');
  const [isLoading, setIsLoading] = useState(false);
  const [notifSettingsOpen, setNotifSettingsOpen] = useState(false);
  const currentUser = useSelector((state) => state.userReducer.user);

  const { 1: projectId, 2: path } = window.location.pathname.split('/');
  useEffect(() => {
    if (isTicketOpen && !selectedTaskParam && taskIdentifier) {
      dispatch(chatOpener(false));
      navigate(
        { search: `?selectedTask=${taskIdentifier}` },
        { replace: true },
      );
    } else if (selectedTaskParam) {
      dispatch(chatOpener(false));
      dispatch(ticketOpener(true));
    } else if (!isTicketOpen) {
      const params = new URLSearchParams(location.search);
      params.delete('selectedTask'); // Remove the specified parameter
      if (isChatOpen && !msgId) {
        params.set('msgId', singleMessage?._id);
      } else if (msgId) {
        dispatch(getSingleMessage(msgId));
        dispatch(chatOpener(true, msgId));
      }

      // Construct the new URL with updated parameters
      const newUrl = `${location.pathname}?${params.toString()}`;

      // Use navigate to replace the current URL
      navigate(newUrl, { replace: true });
    }
  }, [selectedTaskParam, activeChatMessage, projectId, chatId]);

  useEffect(() => {
    if (/null/.test(window.location?.pathname) && !currentWorkspace?._id) {
      navigate('/select-workspace', { replace: true });
      return;
    }
  }, [location.pathname, currentWorkspace?._id]);

  useEffect(() => {
    if (unauthorized) {
      localStorage.removeItem('token');
      localStorage.removeItem('workspaceId');
      localStorage.removeItem('organization');
      localStorage.removeItem('toggle');
      localStorage.removeItem('inviteUrl');
      localStorage.removeItem('Id');

      sessionStorage.removeItem('workspaceId');
      sessionStorage.removeItem('organization');

      auth0Logout({
        logoutParams: { returnTo: `${window.location.origin}/signin` },
      });

      dispatch(logoutAction());
      window.location.href = '/signin';
    }
  }, [unauthorized]);

  useEffect(() => {
    document.title = `${APPLICATION_NAME}`;

    const handleUserVerification = (loggedUser) => {
      setUser(loggedUser);
      const userToBeVerified = new URLSearchParams(location.search).get(
        'verifyUser',
      );
      if (userToBeVerified) {
        const user = {
          _id: userToBeVerified,
          isVerified: true,
        };
        dispatch(
          verifyUser(user, () => {
            const params = new URLSearchParams(location.search);
            params.delete('verifyUser'); // Remove the specified parameter

            // Construct the new URL with updated parameters
            const newUrl = `${location.pathname}?${params.toString()}`;

            // Use navigate to replace the current URL
            navigate(newUrl, { replace: true });

            // setIsUserVerified(true);
            setUser({ ...loggedUser, isVerified: true });
          }),
        );
      }
    };

    // all-tasks defers the dashboard (see the deferred-bootstrap effect below)
    // so the initial /filter isn't queued behind the heavy dashboard build.
    if (!isAllTasksPage) {
      dispatch(getWorkspaceDashboardData());
      dashboardBootstrappedRef.current = true;
    }
    if (phase !== 'activity') {
      dispatch(fetchNotifications());
    }
    // Guard: only fetch if not already in Redux (avoids re-fetch on every route change)
    if (!existingUserRef.current?._id) {
      dispatch(getUser(null, (loggedUser) => handleUserVerification(loggedUser)));
    }
    if (!existingWorkspacesRef.current?.length) {
      dispatch(getWorkspaces('lean'));
    }
  }, []);

  useEffect(() => {
    if (!isAllTasksPage || dashboardBootstrappedRef.current) return;

    const bootstrapDashboard = () => {
      if (dashboardBootstrappedRef.current) return;
      dashboardBootstrappedRef.current = true;
      dispatch(getWorkspaceDashboardData());
    };

    if (userTasksLoaded) {
      bootstrapDashboard();
      return;
    }
    const fallback = setTimeout(bootstrapDashboard, 10000);
    return () => clearTimeout(fallback);
  }, [isAllTasksPage, userTasksLoaded, dispatch]);

  const toggle = useSelector((state) => state.navReducer.toggle);
  const team = useSelector((state) => state.teamReducer.selectedTeam);
  const project = useSelector((state) => state.projectReducer.getProjects);
  const showOnboarding = useSelector(
    (state) => state.onboardingReducer.showOnboarding,
  );

  const showNewTaskModal = useSelector((state) => state.modalReducer.newTaskModal.show);
  const showNewProjectModal = useSelector((state) => state.modalReducer.newProjectModal.show);
  const projectModalTeam = useSelector((state) => state.modalReducer.newProjectModal?.team || '');
  const teamsChatModal = useSelector((state) => state.modalReducer.teamsChatModal);
  const editCustomFieldModal = useSelector((state) => state.modalReducer.editCustomFieldModal);
  const ChartModal = useSelector((state) => state.modalReducer.customChartModal);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = useSelector((state) => state.toggleReducer.isMobile);
  const showNavAsDrawer = windowWidth < 1280;

  const isPanelOpen = isTicketOpen || isChatOpen;
  const panelWidth = (isPanelOpen && !isMobile) ? (drawerWidth || 510) : 0;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('settings') === 'notifications') {
      setNotifSettingsOpen(true);
      params.delete('settings');
      navigate(`${location.pathname}?${params.toString()}`.replace(/\?$/, ''), { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    const url = window.location.href;
    if (!localStorage.getItem('token')) {
      dispatch(setUnauthorized('Session Expired!'));
      navigate('/', { replace: true });
    } else {
      const splitUrl = url.split('inviteId=');
      if (splitUrl.length > 1) {
        const inviteId = splitUrl[1].split('&')?.[0] || '';
        if (inviteId) {
          dispatch(acceptWorkspaceInvite(inviteId)).then((res) => {
            if (res?._id) {
              notification.success({
                message: 'Workspace Joined!',
                placement: 'bottomRight',
              });
              navigate('/', { replace: true });
              dispatch(socketSendAction('invitation-accepted', inviteId));
            }
          });
        }
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (showNavAsDrawer) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (phase == 'project') {
      dispatch(unsetActiveProject());
      dispatch(getProjectById(projectId)).then(async (res) => {
        if (res >= 400) {
          if (res == 403) {
            navigate('/', { replace: true });
            if (
              currentWorkspace?.members?.find(
                (member) => member?._id == user?._id,
              )
            ) {
              // check in workspace
              await dispatch(
                joinProject(
                  {
                    projectId: projectId,
                    role: 'member',
                  },
                  (res) => {
                    if (res?.status != 'pending') {
                      dispatch(getProjectById(projectId));
                      // This is now called in SubNavBar > handleFilterChange
                      // dispatch(getTasksAction(projectId));
                      dispatch(getProjectCustomField(projectId));
                    }
                  },
                ),
              );
            }
            return;
          }
        } else {
          dispatch(getProjectCustomField(projectId));
          // This is now called in SubNavBar > handleFilterChange
          // dispatch(getTasksAction(projectId));
        }
      });
    }

    setSidebarOpen(false);
  }, [projectId, phase]);

  let name = '';

  if (phase === 'team') {
    name = team?.title;
  } else if (phase === 'project') {
    name = project?.title;
  }

  return isLoading ? (
    <div className="h-[100vh] w-[100vw]">
      <Loader newLoader isLoading={isLoading} />
    </div>
  ) : (
    <>
      {/* MODALS */}
      <SettingsModal
        open={notifSettingsOpen}
        onClose={() => setNotifSettingsOpen(false)}
        user={currentUser}
        defaultTab="notifications"
      />
      <WorkspaceFieldsModal
        isModalOpen={workspaceFieldsModal?.show}
        selectedProjectId={workspaceFieldsModal?.selectedProject}
      />
      <WorkspaceTagsModal
        isModalOpen={workspaceTagsModal?.show}
        tags={workspaceTags}
        workspace={currentWorkspace?._id}
      />
      <DuplicateProjectModal
        user={user}
        projects={project}
        isModalOpen={duplicateProjectModal?.show}
        project={duplicateProjectModal?.selectedProject}
      />
      <EditProjectDetailsModal
        isModalOpen={editProjectDetailsModal?.show}
        project={editProjectDetailsModal?.selectedProject}
      />
      <DuplicateTaskModal
        isModalOpen={duplicateTaskModal?.show}  
        task={duplicateTaskModal?.selectedTask}
        />
      <InviteToProjectModal
        isModalOpen={inviteToProjectModal?.show}
        selectedProject={inviteToProjectModal?.selectedProject}
      />
      <PortfolioModal
        isModalOpen={portfolioModal?.show}
        portfolioId={portfolioModal?.portfolioId}
        projectId={portfolioModal?.projectId}
        projects={props.myProjects}
      />
      <EditCustomFieldModal
        isModalOpen={editCustomFieldModal?.show}
        customField={editCustomFieldModal?.selectedCustomField}
        project={editCustomFieldModal?.selectedProject}
      />
      <InviteToWorkspaceModal isModalOpen={inviteToWorkspaceModal?.show} />
      <CustomChartModal isModalOpen={ChartModal?.show} />
      <ChartFullScreenModal isModalOpen={chartFullScreenModal?.show} reportId={chartFullScreenModal?.reportId} />

      <div className="flex" style={{ flex: '1 1 auto', overflow: 'hidden' }}>
        {showNavAsDrawer ? (
          <Drawer
            placement="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            width="auto"
            styles={{ body: { padding: 0, overflow: 'auto', scrollbarWidth: 'none' } }}
            headerStyle={{ display: 'none' }}
          >
            <LeftNavBar
              setSidebarOpen={setSidebarOpen}
              isMobile={showNavAsDrawer}
              setIsLoading={setIsLoading}
            />
          </Drawer>
        ) : (
          <LeftNavBar
            setSidebarOpen={setSidebarOpen}
            isMobile={showNavAsDrawer}
            setIsLoading={setIsLoading}
          />
        )}
        <div
          className="w-[100vw]" // lg:overflow-y-hidden overflow-y-scroll"
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <TopNavbar name={name} projectData={project} phase={phase} setSidebarOpen={setSidebarOpen} />
          <div
            className="sub-container"
            style={{ overflow: 'hidden', flex: 1 }}
          >
            <div style={{ overflow: 'hidden', width: `calc(100% - ${panelWidth}px)`, height: '100%' }}>
              {/* Inner boundary so lazily-loaded pages show a content-area
                  fallback while their chunk loads — the nav/shell stays put
                  so the whole screen is not flashing. */}
              <Suspense fallback={<div style={{ width: '100%', height: '100%', background: '#f4f5fb' }} />}>
                {children}
              </Suspense>
            </div>
            {isChatOpen && <ChatDrawer />}
            {isTicketOpen && <TaskSidePanel customClass="chat-drawer" />}
            <Drawer
              className="drawer"
              headerStyle={{ display: 'none' }}
              onClose={() => {
                dispatch(resetDrawer());
              }}
              styles={{
                padding: 0,
                margin: 0,
              }}
              open={showDrawer}
              width={drawerWidthRef.current}
            >
              {isUserDrawerOpen && <UserDrawer />}
              {showNewProjectModal && <AddProject />}
              {teamsChatModal.show && <AddChatDrawer />}
              {isMemberDrawerOpen?.show && <MemberDrawer />}
              {isAiOpen && <AiSidePanel />}
              {isChatSettingsOpen?.show && (
                <UserChatPanel
                  senderId={isChatSettingsOpen?.chatId}
                  me={user}
                  customClass="chat-drawer"
                />
              )}
              {showNewTaskModal && <AddTaskDrawer />}
            </Drawer>
          </div>
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state) {
  return {
    currentWorkspace: state.workspaceReducer.currentWorkspace,
    projectCustomFields: state.projectReducer.projectCustomFields,
    workspaceTags: state.workspaceReducer.workspaceTags,
    chats: state.chatReducer.chats,
    teams: state.teamReducer.getTeams,
    myProjects: state.projectReducer.myProjects,
  };
}

export default connect(mapStateToProps)(Layout);
